import { useCallback, useEffect, useRef, useState } from "react";

const HOUR = 60 * 60 * 1000;
const FIVE_MINUTES = 5 * 60 * 1000;
const LOCAL_BUILD = "local";

type Options = {
  runningVersion?: string;
  runningBuildId?: string;
};

type ServedBuild = { version: string; buildId?: string };

const isNewerBuild = (served: ServedBuild, version: string, buildId?: string) => {
  if (served.version !== version) return true;
  if (!buildId || !served.buildId) return false;
  if (buildId === LOCAL_BUILD || served.buildId === LOCAL_BUILD) return false;
  return served.buildId !== buildId;
};

const readServedBuild = async (): Promise<ServedBuild | null> => {
  try {
    const res = await fetch("/version.json", { cache: "no-store" });
    if (!res.ok) return null;
    const body = await res.json();
    if (!body || typeof body.version !== "string") return null;
    return {
      version: body.version,
      buildId: typeof body.buildId === "string" ? body.buildId : undefined,
    };
  } catch {
    // Offline, or hosting served index.html for a deploy that predates version.json.
    return null;
  }
};

export function useVersionCheck({
  runningVersion = process.env.REACT_APP_VERSION,
  runningBuildId = process.env.REACT_APP_BUILD_ID,
}: Options = {}) {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const lastCheckedAt = useRef(0);

  useEffect(() => {
    if (!runningVersion) return undefined;
    let cancelled = false;
    let latest = 0;

    // Only the hourly poll brings a dismissed banner back; a tab switch never does.
    const check = async (restoreDismissed: boolean) => {
      const sequence = ++latest;
      lastCheckedAt.current = Date.now();
      const served = await readServedBuild();
      // A slower, older response must not overwrite a newer one.
      if (cancelled || sequence !== latest || !served) return;
      const newer = isNewerBuild(served, runningVersion, runningBuildId);
      setUpdateAvailable(newer);
      if (newer && restoreDismissed) setDismissed(false);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastCheckedAt.current < FIVE_MINUTES) return;
      void check(false);
    };

    void check(false);
    const interval = setInterval(() => void check(true), HOUR);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [runningVersion, runningBuildId]);

  const dismiss = useCallback(() => setDismissed(true), []);

  return { updateAvailable: updateAvailable && !dismissed, dismiss };
}
